import React from 'react';
import PropTypes from 'prop-types';
import {
  Strong,
  ParagraphSecondary,
  ParagraphQuaternary,
  Typography,
  Badge,
} from '@klarna/bubble-ui';
import { connect } from 'react-redux';
import { translator } from 'mage-i18n';
import { analytics } from '../utils/analytics';

import DecimalCurrency from './DecimalCurrency';

const tPrefix = 'product-selection-container.products.products_block';

const styles = {
  container: {
    display: 'flex',
    marginBottom: 11,
    whiteSpace: 'break-spaces',
  },
  originalPrice: { textDecoration: 'none' },
};

const ProductPricing = ({
  disabled = false,
  price: { currencyCode, transactionFee, transactionPercentageFee },
  pricingAnnex,
  showPerTransactionText = true,
  t,
  withFootnoteAsterisk = false,
  small = false,
  badge = false,
  showCrossBorderNotice = false,
}) => {
  // eslint-disable-next-line react/prop-types
  const Paragraph = ({ badge, small, disabled, children }) => {
    if (badge) {
      return (
        <Badge>
          <Typography
            textToken="text-style/text/labels/tiny/medium"
            colorToken="colors/text/inverse"
          >
            {children}
          </Typography>
        </Badge>
      );
    } else if (small) {
      return (
        <ParagraphQuaternary correctFootprint darkGray>
          <Strong>{children}</Strong>
        </ParagraphQuaternary>
      );
    } else {
      return (
        <ParagraphSecondary correctFootprint medium light disabled={disabled}>
          {children}
        </ParagraphSecondary>
      );
    }
  };

  const ANALYTICS_EVENTS = {
    pricingTermsClicked: new analytics.Event({
      name: 'Pricing Terms Clicked',
      description: 'Merchant clicked pricing annex link to see pricing terms',
      action: analytics.EVENT_ACTION.CTA_CLICK,
      category: analytics.EVENT_CATEGORY.FLOW_STEP,
      type: analytics.EVENT_TYPE.USER_PREFERENCE,
      properties: ['pricingAnnex'],
    }),
  };

  return (
    <div style={styles.container}>
      <Paragraph small={small} badge={badge} disabled={disabled}>
        <span style={styles.originalPrice}>
          {`${transactionPercentageFee / 100}% + `}
          <DecimalCurrency value={transactionFee} currency={currencyCode} />
        </span>
        {withFootnoteAsterisk && '*'}
        {!withFootnoteAsterisk && showPerTransactionText
          ? ` (${t(`${tPrefix}.per_transaction`)})`
          : ''}
      </Paragraph>
      {showCrossBorderNotice && (
        <ParagraphQuaternary>
          {'  '}
          {t(`${tPrefix}.cross_border_notice`)}
          {pricingAnnex &&
            `, ${t(`${tPrefix}.cross_border_notice_pricing_annex_suffix.before_link`)} `}
          {pricingAnnex && (
            <a
              href={pricingAnnex}
              rel="noreferrer"
              target="_blank"
              onClick={() =>
                analytics.logEvent(ANALYTICS_EVENTS.pricingTermsClicked, { pricingAnnex })
              }
            >
              {t(`${tPrefix}.cross_border_notice_pricing_annex_suffix.link_text`)}
            </a>
          )}
        </ParagraphQuaternary>
      )}
    </div>
  );
};

ProductPricing.propTypes = {
  price: PropTypes.shape({
    currencyCode: PropTypes.string.isRequired,
    transactionFee: PropTypes.number.isRequired,
    transactionPercentageFee: PropTypes.number.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
  badge: PropTypes.bool,
  disabled: PropTypes.bool,
  pricingAnnex: PropTypes.string,
  showCrossBorderNotice: PropTypes.bool,
  showPerTransactionText: PropTypes.bool,
  small: PropTypes.bool,
  withFootnoteAsterisk: PropTypes.bool,
};

export default connect(state => ({ t: translator(state) }))(ProductPricing);
